import { useEffect, useMemo, useState } from 'react';
import { listInventoryItems, archiveInventoryItem, deleteInventoryItem } from '../api/inventoryApi';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';

const DEFAULT_PAGE_SIZE = 10;

export function useInventoryListScreen() {
  const { triggerExport, isExporting } = useExportJob({
    module: 'inventory',
    fileFormat: 'XLSX',
  });

  const [companyFilter, setCompanyFilter] = useState('All');
  const [branchFilter, setBranchFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [placeFilter, setPlaceFilter] = useState('All');
  const [groupFilter, setGroupFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [valClassFilter, setValClassFilter] = useState('All');
  const [stockTypeFilter, setStockTypeFilter] = useState('As per book');
  const [filterFilter, setFilterFilter] = useState('Show unused items');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [deptStockFilter, setDeptStockFilter] = useState(false);
  const [fromDate, setFromDate] = useState('2026-04-01');
  const [toDate, setToDate] = useState('2027-03-31');

  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      page_size: pageSize,
      viewMode: statusFilter.toLowerCase(),
      searchText,
      companyFilter,
      branchFilter,
      departmentFilter,
      placeFilter,
      groupFilter,
      typeFilter,
      valClassFilter,
      stockTypeFilter,
      filterFilter,
      deptStockFilter,
      fromDate,
      toDate,
    };
  }, [
    page, pageSize, statusFilter, searchText, companyFilter, branchFilter,
    departmentFilter, placeFilter, groupFilter, typeFilter, valClassFilter,
    stockTypeFilter, filterFilter, deptStockFilter, fromDate, toDate
  ]);

  const loadItems = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await listInventoryItems(queryParams);
      const totalCount = response.length;
      const start = (page - 1) * pageSize;
      const paginated = response.slice(start, start + pageSize);
      
      setItems(paginated);
      setMeta({
        totalCount,
        page,
        pageSize,
        hasMore: start + pageSize < totalCount,
      });
    } catch (requestError) {
      setError('Failed to load inventory items.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [queryParams]);

  const handleArchive = async (id) => {
    try {
      await archiveInventoryItem(id);
      loadItems();
    } catch (e) {
      setError('Failed to update inventory item status.');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Inventory Item',
      message: 'Are you sure you want to permanently delete this inventory item?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteInventoryItem(id);
      loadItems();
    } catch (e) {
      setError('Failed to delete inventory item.');
    }
  };

  const resetFilters = () => {
    setCompanyFilter('All');
    setBranchFilter('All');
    setDepartmentFilter('All');
    setPlaceFilter('All');
    setGroupFilter('All');
    setTypeFilter('All');
    setValClassFilter('All');
    setStockTypeFilter('As per book');
    setFilterFilter('Show unused items');
    setStatusFilter('Active');
    setDeptStockFilter(false);
    setFromDate('2026-04-01');
    setToDate('2027-03-31');
    setSearchField('Name');
    setSearchText('');
    setPage(1);
  };

  const actions = {
    setCompanyFilter,
    setBranchFilter,
    setDepartmentFilter,
    setPlaceFilter,
    setGroupFilter,
    setTypeFilter,
    setValClassFilter,
    setStockTypeFilter,
    setFilterFilter,
    setStatusFilter,
    setDeptStockFilter,
    setFromDate,
    setToDate,
    setSearchField,
    setSearchText,
    setPage,
    resetFilters,
    handleArchive,
    handleDelete,
    refresh: loadItems,
    triggerExport,
  };

  return {
    state: {
      companyFilter,
      branchFilter,
      departmentFilter,
      placeFilter,
      groupFilter,
      typeFilter,
      valClassFilter,
      stockTypeFilter,
      filterFilter,
      statusFilter,
      deptStockFilter,
      fromDate,
      toDate,
      searchField,
      searchText,
      page,
      pageSize,
      isLoading,
      isExporting,
      error,
      rows: items,
      meta,
    },
    actions,
  };
}
